import React, { useState, useEffect } from "react";
import {
  Box, Grid, Paper, Typography, Stack, Chip,
  IconButton, Tooltip, Alert, TextField, Button,
} from "@mui/material";
import {
  Analytics, CheckCircle, Cancel, Public, TrackChanges, Share, OpenInNew, Save,
} from "@mui/icons-material";
import { apiClient } from "../../services/authService";
import { DOMAIN, PAGE_LINKS, CopyButton } from "./marketingShared";

// ─── Constants ───────────────────────────────────────────────────────────────

const TRACKING_STORAGE_KEY = "tracking_ids_config";

const ADSENSE_ID = "ca-pub-3544612181938151";

const SITE_META = {
  ogTitle:       "Dr.Paw — Pet Care Courses for Pet Parents",
  ogDescription: "Vet-written pet-care courses on nutrition, behavior, health and safety — made simple for pet parents. Learn with Dr.Paw.",
  ogImage:       "https://drpawleb.com/og-image.png",
  canonical:     "https://drpawleb.com/",
  twitterCard:   "summary_large_image",
  twitterSite:   "@drpawleb",
};

const TRACKED_EVENTS = [
  { event: "page_view",       label: "Page View",         ga4: true,  pixel: true,  color: "#1565C0", desc: "Every route navigation — fired automatically on each page" },
  { event: "plan_selected",   label: "Plan Selected",     ga4: true,  pixel: true,  color: "#0F766E", desc: "User clicks Subscribe / Secure My Spot on the pricing page" },
  { event: "sign_up",         label: "Sign Up Complete",  ga4: true,  pixel: true,  color: "#7C3AED", desc: "User successfully completes account registration" },
  { event: "purchase",        label: "Purchase",          ga4: true,  pixel: true,  color: "#D97706", desc: "User completes subscription payment — includes value & plan name" },
  { event: "share",           label: "Share Content",     ga4: true,  pixel: true,  color: "#E1306C", desc: "User shares any public page (quiz, poll, tip, case, countdown…)" },
  { event: "generate_lead",   label: "WhatsApp Lead",     ga4: true,  pixel: true,  color: "#25D366", desc: "User submits 'Notify Me' form on a countdown page via WhatsApp" },
  { event: "quiz_complete",   label: "Quiz Complete",     ga4: true,  pixel: false, color: "#1E40AF", desc: "User finishes a quiz and sees their score — GA4 only" },
  { event: "countdown_view",  label: "Countdown View",    ga4: true,  pixel: true,  color: "#B91C1C", desc: "User views a countdown/launch page — top-of-funnel signal" },
];

const PAGE_META_LIST = [
  { label: "Home",    path: "/",                  url: `${DOMAIN}/`,                        title: "Dr.Paw — Pet Care Courses for Pet Parents",       description: "Vet-written pet-care courses for pet parents — nutrition, behavior, health and safety, explained simply. Learn to care for your pet with Dr.Paw." },
  { label: "Courses", path: "/membership/classes",url: `${DOMAIN}/membership/classes`,     title: "Pet Care Courses — Dr.Paw",                       description: "Browse vet-written pet-care courses on nutrition, behavior, health, safety and everyday pet parenting." },
  { label: "Pricing", path: "/#pricing",           url: `${DOMAIN}/#pricing`,               title: "Plans & Pricing — Dr.Paw",                        description: "Choose your pet-care learning plan. Buy individual courses or get full access to the library." },
  { label: "Login",   path: "/login",              url: `${DOMAIN}/login`,                  title: "Login — Dr.Paw",                                  description: "Sign in to access your Dr.Paw pet-care courses." },
  { label: "Sign Up", path: "/signup",             url: `${DOMAIN}/signup`,                 title: "Join Dr.Paw — Free Registration",                 description: "Create your free Dr.Paw account and start learning how to care for your pet today." },
];

const BASE_SEO_CHECKLIST = [
  { label: "Title tag",              ok: true,  note: "65 chars" },
  { label: "Meta description",       ok: true,  note: "156 chars" },
  { label: "Canonical URL",          ok: true,  note: "Set to /" },
  { label: "robots: index,follow",   ok: true,  note: "Default" },
  { label: "og:title",               ok: true,  note: "Social ready" },
  { label: "og:description",         ok: true,  note: "Social ready" },
  { label: "og:image (1200×630)",    ok: true,  note: "/og-image.png" },
  { label: "Twitter Card",           ok: true,  note: "summary_large_image" },
  { label: "JSON-LD Schema",         ok: true,  note: "Org + WebSite + Edu" },
  { label: "Favicon",                ok: true,  note: "logo.png" },
];

const PLATFORMS = [
  { name: "Google Analytics 4",    sub: "Real-time traffic, audience, events",      icon: "📊", color: "#E37400", url: "https://analytics.google.com" },
  { name: "Meta Business Suite",   sub: "Pixel events, audiences & ad performance", icon: "📘", color: "#1877F2", url: "https://business.facebook.com" },
  { name: "Google Search Console", sub: "SEO impressions, keywords & index status", icon: "🔍", color: "#34A853", url: "https://search.google.com/search-console" },
  { name: "Google AdSense",        sub: "Ad revenue, RPM & impression data",        icon: "💰", color: "#FBBC04", url: "https://www.google.com/adsense" },
];

// ─────────────────────────────────────────────────────────────────────────────

const AdminMarketingAnalytics: React.FC = () => {
  const [gaId, setGaId]       = useState("");
  const [pixelId, setPixelId] = useState("");
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  useEffect(() => {
    apiClient.get<{ value: { gaId?: string; metaPixelId?: string } }>("/settings/tracking-ids")
      .then(res => {
        if (res.data?.value) {
          setGaId(res.data.value.gaId || "");
          setPixelId(res.data.value.metaPixelId || "");
        }
      })
      .catch(() => {
        const local = localStorage.getItem(TRACKING_STORAGE_KEY);
        if (local) {
          try {
            const parsed = JSON.parse(local);
            setGaId(parsed.gaId || "");
            setPixelId(parsed.metaPixelId || "");
          } catch {}
        }
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const value = { gaId: gaId.trim(), metaPixelId: pixelId.trim() };
    localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(value));
    try {
      await apiClient.put("/settings/tracking-ids", { value });
    } catch {
      // localStorage fallback already saved
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const gaConfigured = !!gaId.trim();
  const pixelConfigured = !!pixelId.trim();

  const TRACKERS = [
    { name: "Google Analytics 4",              id: gaId,    placeholder: "G-XXXXXXXXXX",       configured: gaConfigured,    badge: "GA4",  color: "#E37400", link: "https://analytics.google.com",   onChange: setGaId },
    { name: "Meta Pixel (Facebook/Instagram)",  id: pixelId, placeholder: "XXXXXXXXXXXXXXXXX",  configured: pixelConfigured, badge: "META", color: "#1877F2", link: "https://business.facebook.com",  onChange: setPixelId },
  ];

  const SEO_CHECKLIST = [
    ...BASE_SEO_CHECKLIST,
    { label: "Google Analytics 4", ok: gaConfigured,    note: gaConfigured ? "Configured" : "ID not configured" },
    { label: "Meta Pixel",         ok: pixelConfigured, note: pixelConfigured ? "Configured" : "ID not configured" },
  ];

  const passingCount = SEO_CHECKLIST.filter((x) => x.ok).length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>

      {/* ── Page Hero ──────────────────────────────────────────────────────── */}
      <Box sx={{
        mb: 4, p: { xs: 2.5, md: 3.5 }, borderRadius: "20px",
        background: "linear-gradient(135deg,#0A1628 0%,#0D47A1 60%,#1565C0 100%)",
        boxShadow: "0 8px 32px rgba(10,22,40,.35)",
      }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(255,255,255,.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Analytics sx={{ color: "#fff", fontSize: "1.6rem" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>Analytics & SEO</Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,.65)", mt: 0.3 }}>
                Manage tracking IDs, monitor SEO health, and inspect all site meta tags
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={`${TRACKED_EVENTS.length} Events Tracked`} size="small" sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff", fontWeight: 600, fontSize: ".72rem" }} />
            <Chip label={`${passingCount}/${SEO_CHECKLIST.length} SEO Checks`} size="small" sx={{ bgcolor: passingCount === SEO_CHECKLIST.length ? "rgba(34,197,94,.25)" : "rgba(251,191,36,.25)", color: "#fff", fontWeight: 600, fontSize: ".72rem" }} />
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={3}>

        {/* ── Left Column ─────────────────────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 5 }}>

          {/* Analytics Configuration */}
          <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
            <Box sx={{ px: 2.5, py: 2, bgcolor: "#0A1628" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Analytics sx={{ color: "#fff" }} />
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Analytics Configuration</Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,.5)" }}>Set your real tracking IDs — takes effect immediately, no code changes needed</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {saved && (
                <Alert severity="success" sx={{ mb: 1.5, borderRadius: "10px", "& .MuiAlert-message": { fontSize: ".8rem" } }}>
                  Tracking IDs saved.
                </Alert>
              )}
              {TRACKERS.map((tracker) => (
                <Box key={tracker.name} sx={{ p: 1.5, mb: 1.5, borderRadius: "12px", border: `1px solid ${tracker.configured ? "#D1FAE5" : "#FEE2E2"}`, bgcolor: tracker.configured ? "#F0FDF4" : "#FFF5F5", "&:last-child": { mb: 0 } }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.2 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: "9px", bgcolor: tracker.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: ".65rem", letterSpacing: ".03em" }}>{tracker.badge}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.4, flexWrap: "wrap" }}>
                        <Typography sx={{ fontWeight: 700, fontSize: ".83rem", color: "#0A1628" }}>{tracker.name}</Typography>
                        <Chip
                          size="small"
                          icon={tracker.configured ? <CheckCircle sx={{ fontSize: ".75rem !important" }} /> : <Cancel sx={{ fontSize: ".75rem !important" }} />}
                          label={tracker.configured ? "Active" : "Not Configured"}
                          sx={{ height: 20, fontSize: ".65rem", fontWeight: 700, bgcolor: tracker.configured ? "#DCFCE7" : "#FEE2E2", color: tracker.configured ? "#15803D" : "#DC2626", "& .MuiChip-icon": { color: tracker.configured ? "#15803D" : "#DC2626" } }}
                        />
                      </Stack>
                    </Box>
                    <Tooltip title="Open dashboard">
                      <IconButton size="small" onClick={() => window.open(tracker.link, "_blank")} sx={{ color: "#78909C", borderRadius: "6px", flexShrink: 0 }}>
                        <OpenInNew sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={tracker.placeholder}
                      value={tracker.id}
                      onChange={(e) => tracker.onChange(e.target.value)}
                      sx={{ bgcolor: "#fff", borderRadius: "8px", "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: ".8rem" } }}
                    />
                    <CopyButton text={tracker.id} small />
                  </Stack>
                  {!tracker.configured && (
                    <Alert severity="warning" sx={{ mt: 1, py: 0.3, "& .MuiAlert-message": { fontSize: ".75rem", py: 0 } }}>
                      Enter your real ID above and save to start tracking.
                    </Alert>
                  )}
                </Box>
              ))}

              {/* AdSense — static, configured in public/index.html */}
              <Box sx={{ p: 1.5, mb: 1.5, borderRadius: "12px", border: "1px solid #D1FAE5", bgcolor: "#F0FDF4" }}>
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                  <Box sx={{ width: 38, height: 38, borderRadius: "9px", bgcolor: "#34A853", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: ".65rem", letterSpacing: ".03em" }}>ADS</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.4, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 700, fontSize: ".83rem", color: "#0A1628" }}>Google AdSense</Typography>
                      <Chip size="small" icon={<CheckCircle sx={{ fontSize: ".75rem !important" }} />} label="Active"
                        sx={{ height: 20, fontSize: ".65rem", fontWeight: 700, bgcolor: "#DCFCE7", color: "#15803D", "& .MuiChip-icon": { color: "#15803D" } }} />
                    </Stack>
                    <Typography variant="caption" sx={{ color: "#78909C", fontFamily: "monospace", fontSize: ".75rem", display: "block", wordBreak: "break-all" }}>{ADSENSE_ID}</Typography>
                  </Box>
                  <CopyButton text={ADSENSE_ID} small />
                </Stack>
              </Box>

              <Button
                fullWidth
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={saving}
                sx={{ mt: 0.5, bgcolor: "#1565C0", textTransform: "none", fontWeight: 600, borderRadius: "10px", "&:hover": { bgcolor: "#0D47A1" } }}
              >
                {saving ? "Saving…" : "Save Tracking IDs"}
              </Button>
            </Box>
          </Paper>

          {/* Quick Access */}
          <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
            <Box sx={{ px: 2.5, py: 2, bgcolor: "#F0F7FF" }}>
              <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: ".95rem" }}>Quick Access — Analytics Platforms</Typography>
              <Typography variant="caption" sx={{ color: "#78909C" }}>Click any card to open in a new tab</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {PLATFORMS.map((p) => (
                <Box key={p.name} onClick={() => window.open(p.url, "_blank")} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.2, mb: 1, borderRadius: "10px", border: "1px solid #E8EDF5", cursor: "pointer", transition: "all .2s", "&:hover": { borderColor: p.color, bgcolor: "#F8FAFF", transform: "translateX(3px)" }, "&:last-child": { mb: 0 } }}>
                  <Typography sx={{ fontSize: "1.5rem", flexShrink: 0, lineHeight: 1 }}>{p.icon}</Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: ".85rem", color: "#0A1628" }}>{p.name}</Typography>
                    <Typography variant="caption" sx={{ color: "#78909C" }}>{p.sub}</Typography>
                  </Box>
                  <OpenInNew sx={{ fontSize: "1rem", color: "#CBD5E1", flexShrink: 0 }} />
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Tracked Events */}
          <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
            <Box sx={{ px: 2.5, py: 2, bgcolor: "#1565C0" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TrackChanges sx={{ color: "#fff" }} />
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Event Tracking — {TRACKED_EVENTS.length} Events</Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,.6)" }}>All events defined in src/analytics.ts</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {TRACKED_EVENTS.map((ev) => (
                <Box key={ev.event} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.2, mb: 1, borderRadius: "10px", bgcolor: "#F8FAFF", border: "1px solid #E8EDF5", "&:last-child": { mb: 0 } }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: ev.color, flexShrink: 0, mt: 0.75 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.3, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 700, fontSize: ".8rem", color: "#0A1628", fontFamily: "monospace" }}>{ev.event}</Typography>
                      {ev.ga4   && <Chip label="GA4"   size="small" sx={{ height: 16, fontSize: ".6rem", fontWeight: 700, bgcolor: "#FEF3C7", color: "#92400E" }} />}
                      {ev.pixel && <Chip label="Pixel" size="small" sx={{ height: 16, fontSize: ".6rem", fontWeight: 700, bgcolor: "#EFF6FF", color: "#1D4ED8" }} />}
                    </Stack>
                    <Typography variant="caption" sx={{ color: "#78909C", lineHeight: 1.5 }}>{ev.desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* ── Right Column ────────────────────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 7 }}>

          {/* Site Pages Overview */}
          <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
            <Box sx={{ px: 2.5, py: 2, bgcolor: "#0A1628" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Public sx={{ color: "#fff" }} />
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Site Pages — URL & Meta Overview</Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,.5)" }}>All public pages with shareable URLs and SEO metadata</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {PAGE_META_LIST.map((page, i) => (
                <Box key={page.path} sx={{ p: 1.8, borderRadius: "12px", border: "1px solid #E8EDF5", bgcolor: "#FAFCFF", mb: i < PAGE_META_LIST.length - 1 ? 1.5 : 0 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                    <Box sx={{ width: 36, height: 36, borderRadius: "9px", bgcolor: "#EEF4FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.2 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: ".72rem", color: "#1565C0" }}>{page.label.slice(0, 2).toUpperCase()}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.4, flexWrap: "wrap" }}>
                        <Typography sx={{ fontWeight: 700, fontSize: ".9rem", color: "#0A1628" }}>{page.label}</Typography>
                        <Chip label="PUBLIC" size="small" sx={{ height: 16, fontSize: ".6rem", fontWeight: 700, bgcolor: "#DCFCE7", color: "#15803D" }} />
                      </Stack>
                      <Typography variant="caption" sx={{ color: "#1565C0", fontFamily: "monospace", display: "block", mb: 0.5, wordBreak: "break-all" }}>{page.url}</Typography>
                      <Typography sx={{ fontSize: ".82rem", fontWeight: 600, color: "#0A1628", mb: 0.3 }}>{page.title}</Typography>
                      <Typography variant="caption" sx={{ color: "#78909C", lineHeight: 1.5, display: "block" }}>{page.description}</Typography>
                    </Box>
                    <Stack direction="column" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <CopyButton text={page.url} small />
                      <Tooltip title="Open page">
                        <IconButton size="small" onClick={() => window.open(page.url, "_blank")} sx={{ color: "#78909C", borderRadius: "6px" }}>
                          <OpenInNew sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Open Graph & Social Preview */}
          <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
            <Box sx={{ px: 2.5, py: 2, background: "linear-gradient(135deg,#833AB4,#E1306C)" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Share sx={{ color: "#fff" }} />
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Open Graph & Social Preview</Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,.7)" }}>How drpawleb.com appears when shared on WhatsApp, Facebook & LinkedIn</Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: ".82rem", color: "#455A64", mb: 1.5 }}>
                Link card preview — WhatsApp / Facebook:
              </Typography>
              <Box sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #E2E8F0", maxWidth: 440, mb: 2.5 }}>
                <Box sx={{ height: 140, position: "relative", background: "linear-gradient(135deg,#0A1628 0%,#1565C0 60%,#42A5F5 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "2.2rem", letterSpacing: "-0.04em" }}>Dr.Paw</Typography>
                  <Chip label="og:image" size="small" sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,.55)", color: "#fff", fontSize: ".6rem", fontWeight: 700 }} />
                </Box>
                <Box sx={{ p: 1.5, bgcolor: "#F8FAFF", borderTop: "1px solid #E2E8F0" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: ".82rem", color: "#0A1628", mb: 0.3 }}>{SITE_META.ogTitle}</Typography>
                  <Typography variant="caption" sx={{ color: "#455A64", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{SITE_META.ogDescription}</Typography>
                  <Typography variant="caption" sx={{ color: "#94A3B8", display: "block", mt: 0.5 }}>drpawleb.com</Typography>
                </Box>
              </Box>

              <Typography sx={{ fontWeight: 700, fontSize: ".82rem", color: "#455A64", mb: 1 }}>All Open Graph &amp; Twitter meta tags:</Typography>
              <Stack spacing={0.8}>
                {[
                  { tag: "og:title",       value: SITE_META.ogTitle },
                  { tag: "og:description", value: SITE_META.ogDescription },
                  { tag: "og:image",       value: SITE_META.ogImage },
                  { tag: "og:type",        value: "website" },
                  { tag: "og:url",         value: SITE_META.canonical },
                  { tag: "og:site_name",   value: "Dr.Paw" },
                  { tag: "twitter:card",   value: SITE_META.twitterCard },
                  { tag: "twitter:site",   value: SITE_META.twitterSite },
                  { tag: "twitter:title",  value: SITE_META.ogTitle },
                ].map((meta) => (
                  <Box key={meta.tag} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1, borderRadius: "8px", bgcolor: "#F8FAFF", border: "1px solid #E8EDF5" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: ".73rem", color: "#E1306C", fontFamily: "monospace", flexShrink: 0, width: 140, pt: 0.1 }}>{meta.tag}</Typography>
                    <Typography variant="caption" sx={{ color: "#455A64", flex: 1, wordBreak: "break-all", lineHeight: 1.5 }}>{meta.value}</Typography>
                    <CopyButton text={meta.value} small />
                  </Box>
                ))}
              </Stack>
            </Box>
          </Paper>

          {/* SEO Health Checklist */}
          <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
            <Box sx={{ px: 2.5, py: 2, bgcolor: "#F0FDF4" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: ".95rem" }}>SEO Health Checklist</Typography>
                  <Typography variant="caption" sx={{ color: "#78909C" }}>Technical SEO status for drpawleb.com</Typography>
                </Box>
                <Chip label={`${passingCount}/${SEO_CHECKLIST.length} Passing`} sx={{ fontWeight: 700, fontSize: ".75rem", bgcolor: passingCount === SEO_CHECKLIST.length ? "#DCFCE7" : "#FEF3C7", color: passingCount === SEO_CHECKLIST.length ? "#15803D" : "#92400E" }} />
              </Stack>
            </Box>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={1}>
                {SEO_CHECKLIST.map((item) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={item.label}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1, borderRadius: "9px", bgcolor: item.ok ? "#F0FDF4" : "#FFF5F5", border: `1px solid ${item.ok ? "#D1FAE5" : "#FEE2E2"}`, height: "100%" }}>
                      {item.ok
                        ? <CheckCircle sx={{ fontSize: "1.1rem", color: "#16A34A", flexShrink: 0 }} />
                        : <Cancel     sx={{ fontSize: "1.1rem", color: "#DC2626", flexShrink: 0 }} />
                      }
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: ".78rem", fontWeight: 700, color: item.ok ? "#065F46" : "#991B1B", lineHeight: 1.3 }}>{item.label}</Typography>
                        <Typography variant="caption" sx={{ color: item.ok ? "#059669" : "#DC2626", opacity: 0.85, fontSize: ".68rem" }}>{item.note}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                ))}
              </Grid>

              {(!gaConfigured || !pixelConfigured) && (
                <Alert severity="warning" sx={{ mt: 2, "& .MuiAlert-message": { fontSize: ".8rem" } }}>
                  <strong>Activate Analytics:</strong> Enter your GA4 Measurement ID and Meta Pixel ID in the "Analytics Configuration" card above and save — no code changes or redeploy needed.
                </Alert>
              )}
            </Box>
          </Paper>

        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminMarketingAnalytics;
